import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useStaffAuth } from './StaffAuthContext';
import { captureDesktopRedirect } from './staffDesktopRedirect';
import { requestedStaffAdminPath, staffAdminReturnState } from './staffAdminReturnPath';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .sl-root {
    min-height: 100vh;
    background: #080B12;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    position: relative;
    overflow: hidden;
  }

  .sl-bg-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(201,168,76,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(201,168,76,0.04) 1px, transparent 1px);
    background-size: 60px 60px;
    pointer-events: none;
  }

  .sl-bg-glow {
    position: absolute;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  .sl-card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 440px;
    margin: 24px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 20px;
    padding: 48px 40px;
    backdrop-filter: blur(20px);
    animation: sl-fadeup 0.6s ease both;
  }

  @keyframes sl-fadeup {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .sl-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 36px;
  }

  .sl-logo-mark {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #C9A84C, #8B0000);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 18px;
    color: #fff;
    letter-spacing: -1px;
    flex-shrink: 0;
  }

  .sl-logo-text {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 18px;
    color: #fff;
    letter-spacing: 0.5px;
  }

  .sl-logo-sub {
    font-size: 10px;
    font-weight: 500;
    color: #C9A84C;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 1px;
  }

  .sl-heading {
    font-family: 'Syne', sans-serif;
    font-size: 26px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 6px;
    letter-spacing: -0.5px;
  }

  .sl-sub {
    font-size: 13px;
    color: rgba(255,255,255,0.4);
    margin-bottom: 36px;
    line-height: 1.5;
  }

  .sl-field {
    margin-bottom: 16px;
  }

  .sl-label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255,255,255,0.5);
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .sl-input {
    width: 100%;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    padding: 13px 16px;
    font-size: 15px;
    font-family: 'DM Sans', sans-serif;
    color: #fff;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
  }

  .sl-input:focus {
    border-color: rgba(201,168,76,0.5);
    background: rgba(201,168,76,0.04);
  }

  .sl-input::placeholder { color: rgba(255,255,255,0.2); }

  .sl-btn {
    width: 100%;
    margin-top: 24px;
    padding: 14px;
    background: linear-gradient(135deg, #C9A84C, #A0832E);
    border: none;
    border-radius: 10px;
    font-family: 'Syne', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #080B12;
    cursor: pointer;
    letter-spacing: 0.3px;
    transition: opacity 0.2s, transform 0.1s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .sl-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  .sl-btn:active:not(:disabled) { transform: translateY(0); }
  .sl-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .sl-error {
    margin-top: 16px;
    padding: 12px 16px;
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.25);
    border-radius: 10px;
    font-size: 13px;
    color: #FCA5A5;
    line-height: 1.5;
  }

  .sl-security {
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid rgba(255,255,255,0.06);
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(255,255,255,0.25);
    font-size: 11px;
    letter-spacing: 0.3px;
  }

  .sl-security svg { flex-shrink: 0; }

  .sl-forgot {
    display: block;
    text-align: right;
    font-size: 12px;
    color: rgba(201,168,76,0.7);
    text-decoration: none;
    margin-top: 8px;
    transition: color 0.2s;
  }
  .sl-forgot:hover { color: #C9A84C; }

  .sl-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(8,11,18,0.3);
    border-top-color: #080B12;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  @media (max-width: 480px) {
    .sl-card { padding: 36px 24px; }
  }
`;

export default function StaffLogin() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const { staffUser, staffLogin, loading: authLoading } = useStaffAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = requestedStaffAdminPath(location);
  const returnState = staffAdminReturnState(returnTo);

  // DCC desktop sign-in bridge: if /v1/desktop/authorize bounced a staff account here, remember the
  // approved VISINVOKE return URL so the dashboard can resume it after MFA (single desktop login).
  useEffect(() => { captureDesktopRedirect(); }, []);

  useEffect(() => {
    if (!authLoading && staffUser) {
      navigate(returnTo, { replace: true });
    }
  }, [staffUser, authLoading, navigate, returnTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setError('');
    setLoading(true);
    try {
      const data = await staffLogin(email.trim().toLowerCase(), password);
      if (data.status === 'mfa_setup_required') {
        navigate('/admin/mfa-setup', { replace: true, state: returnState });
      } else if (data.status === 'mfa_required') {
        navigate('/admin/mfa-verify', { replace: true, state: returnState });
      } else if (data.status === 'authenticated') {
        if (data.role_selection_required) {
          navigate('/admin/select-role', { replace: true, state: returnState });
        } else {
          navigate(returnTo, { replace: true });
        }
      }
    } catch (err) {
      const msg = err?.data?.error || '';
      if (msg.includes('Too many')) {
        setError('Too many failed attempts. Please wait before trying again.');
      } else if (err?.status === 429) {
        setError('Too many failed attempts. Please wait before trying again.');
      } else if (err?.status >= 500) {
        setError('Service unavailable. Please try again shortly.');
      } else {
        setError('Invalid email or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="sl-root">
        <div className="sl-bg-grid" />
        <div className="sl-bg-glow" />
        <div className="sl-card">
          <div className="sl-logo">
            <div className="sl-logo-mark">VI</div>
            <div>
              <div className="sl-logo-text">VISINVOKE</div>
              <div className="sl-logo-sub">Staff Access</div>
            </div>
          </div>

          <h1 className="sl-heading">Staff Login</h1>
          <p className="sl-sub">Authorized VISINVOKE staff access only.</p>

          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="sl-field">
              <label className="sl-label">Email Address</label>
              <input
                className="sl-input"
                type="email"
                placeholder="staff@visinvoke.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoFocus
                autoComplete="username"
                disabled={loading}
              />
            </div>
            <div className="sl-field">
              <label className="sl-label">Password</label>
              <input
                className="sl-input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            <Link to="/admin/reset-password" className="sl-forgot">Forgot password?</Link>

            {error && <div className="sl-error">{error}</div>}

            <button className="sl-btn" type="submit" disabled={loading}>
              {loading ? <><div className="sl-spinner" /> Verifying...</> : 'Secure Staff Login'}
            </button>
          </form>

          <div className="sl-security">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            All staff sessions are monitored and logged. MFA required.
          </div>
        </div>
      </div>
    </>
  );
}
